import React, { useState } from 'react'

const formatTimestamp = (value) => {
  if (!value) {
    return '—'
  }
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return '—'
  }
  return date.toLocaleTimeString()
}

const formatJsonOutput = (data) => {
  // Helper to recursively parse stringified JSON fields
  const deepParse = (obj) => {
    if (typeof obj === 'string') {
      try {
        const parsed = JSON.parse(obj)
        return deepParse(parsed) // Recursively parse in case of nested JSON strings
      } catch {
        return obj
      }
    }

    if (Array.isArray(obj)) {
      return obj.map(item => deepParse(item))
    }

    if (obj !== null && typeof obj === 'object') {
      const result = {}
      for (const [key, value] of Object.entries(obj)) {
        result[key] = deepParse(value)
      }
      return result
    }

    return obj
  }

  // Unwrap common envelope structures to reduce nesting
  const unwrap = (obj) => {
    if (!obj || typeof obj !== 'object') {
      return obj
    }

    // If object has only one property and it looks like a wrapper, unwrap it
    const keys = Object.keys(obj)

    // Check for common CEP/Esper wrapper patterns
    // If there's a csvPayload or jsonPayload, prefer that over the wrapper
    if (obj.csvPayload) {
      return deepParse(obj.csvPayload)
    }
    if (obj.jsonPayload) {
      return deepParse(obj.jsonPayload)
    }

    // If there's a 'data' or 'payload' field that contains the real data, unwrap it
    if (keys.length === 1 && (keys[0] === 'data' || keys[0] === 'payload')) {
      return unwrap(obj[keys[0]])
    }

    // Remove metadata/envelope fields that aren't useful for viewing
    const filtered = { ...obj }
    const fieldsToRemove = ['cepInstance', 'statementName', 'metadata']
    fieldsToRemove.forEach(field => delete filtered[field])

    return filtered
  }

  const parsed = deepParse(data)
  const unwrapped = unwrap(parsed)
  return JSON.stringify(unwrapped, null, 2)
}

const StatementOutput = ({ validationLog = [], events = [], activeStatement }) => {
  const [isHistoryExpanded, setIsHistoryExpanded] = useState(false)

  const latestEvent = events[0]
  const olderEvents = events.slice(1, 20)

  return (
    <div className="statements-panel__output-placeholder">
      <section className="statements-panel__output-section">
        <h4>Latest Output</h4>
        {events.length === 0 ? (
          <p style={{ color: 'var(--as-text-muted)', fontSize: '0.9rem' }}>
            No runtime events received for the current selection.
          </p>
        ) : (
          <>
            <div className="statements-panel__output-latest">
              <div className="statements-panel__output-meta">
                {formatTimestamp(latestEvent.receivedAt)} • {latestEvent.statementName ?? activeStatement?.statementName ?? 'Statement'}
              </div>
              <pre className="statements-panel__output-payload" style={{
                fontSize: '13px',
                lineHeight: '1.6',
                padding: '16px',
                overflow: 'auto',
                maxHeight: '600px'
              }}>
                {formatJsonOutput(latestEvent.raw?.data ?? latestEvent)}
              </pre>
            </div>

            {olderEvents.length > 0 && (
              <div className="statements-panel__output-history">
                <button
                  type="button"
                  className="statements-panel__history-toggle"
                  onClick={() => setIsHistoryExpanded(!isHistoryExpanded)}
                  aria-expanded={isHistoryExpanded}
                >
                  {isHistoryExpanded ? '▼' : '▶'} Previous outputs ({olderEvents.length})
                </button>

                {isHistoryExpanded && (
                  <ul className="statements-panel__output-list">
                    {olderEvents.map((event) => (
                      <li key={event.id} className="statements-panel__output-item">
                        <span className="statements-panel__output-meta">
                          {formatTimestamp(event.receivedAt)} • {event.statementName ?? activeStatement?.statementName ?? 'Statement'}
                        </span>
                        <pre className="statements-panel__output-payload" style={{
                          fontSize: '13px',
                          lineHeight: '1.6',
                          padding: '16px',
                          overflow: 'auto',
                          maxHeight: '400px'
                        }}>
                          {formatJsonOutput(event.raw?.data ?? event)}
                        </pre>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </>
        )}
      </section>
    </div>
  )
}

export default StatementOutput

